const COPY = Object.freeze({
  SERVICEABLE: 'Delivery is available to this PIN code.',
  NOT_SERVICEABLE: 'Delivery is currently unavailable to this PIN code.',
  TEMPORARILY_UNAVAILABLE: 'Delivery is temporarily unavailable for this PIN code. Please try again later.',
  INVALID_PINCODE: 'Enter a valid 6-digit PIN code.',
  ERROR_RETRY: 'Unable to check delivery right now. Please try again.',
});

const ANALYTICS_EVENT = Object.freeze({
  SERVICEABLE: 'delivery_check_success',
  NOT_SERVICEABLE: 'delivery_check_unavailable',
  TEMPORARILY_UNAVAILABLE: 'delivery_check_unavailable',
  INVALID_PINCODE: 'delivery_check_error',
  ERROR_RETRY: 'delivery_check_error',
});

class DeliveryChecker extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('input[name="pincode"]');
    this.button = this.querySelector('button[type="button"]');
    this.status = this.querySelector('[aria-live]');
    this.revision = 0;
    this.button?.addEventListener('click', (event) => this.submit(event));
    this.input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
      this.submit(event);
    });
    this.input?.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/[^0-9]/g, '').slice(0, 6);
      this.revision += 1;
      this.input.removeAttribute('aria-invalid');
      this.status.textContent = '';
      delete this.status.dataset.state;
    });
  }

  emit(name, detail = {}) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }

  render(status) {
    const safeStatus = COPY[status] ? status : 'ERROR_RETRY';
    this.status.textContent = COPY[safeStatus];
    this.status.dataset.state = safeStatus;
    this.input.setAttribute('aria-invalid', String(safeStatus === 'INVALID_PINCODE'));
    this.emit(ANALYTICS_EVENT[safeStatus], { status: safeStatus });
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy) return;
    const pincode = this.input.value;
    if (!/^[0-9]{6}$/.test(pincode)) {
      this.render('INVALID_PINCODE');
      return;
    }

    this.busy = true;
    const submittedRevision = this.revision;
    this.button.disabled = true;
    this.button.textContent = 'Checking…';
    this.status.textContent = 'Checking delivery availability…';
    this.status.dataset.state = 'LOADING';
    this.emit('delivery_check_started');

    try {
      const endpoint = new URL(this.dataset.endpoint || '/apps/delivery-check', window.location.origin);
      endpoint.searchParams.set('pincode', pincode);
      const response = await fetch(endpoint, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (data?.pincode !== pincode || !COPY[data?.status]) throw new Error('Invalid normalized response');
      if (submittedRevision !== this.revision) return;
      this.render(data?.status);
    } catch {
      if (submittedRevision !== this.revision) return;
      this.render('ERROR_RETRY');
    } finally {
      this.busy = false;
      this.button.disabled = false;
      this.button.textContent = 'Check';
    }
  }
}

if (!customElements.get('delivery-checker')) customElements.define('delivery-checker', DeliveryChecker);
